import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { EarTrainingPayload, EarTrainingRuntime } from '../../core/types/payloads/ear_training.types';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import {
  normalizeRhythmPattern,
  normalizeTokenArray,
  patternToTeacherScript,
} from '../../core/music/tokens';
import { frequenciesFromNoteNames } from '../../core/music/webAudio';
import { DEFAULT_MUSIC_PREFS } from '../../core/types/music';

export function validateEarTrainingPayload(raw: unknown): EarTrainingPayload {
  if (!raw || typeof raw !== 'object') throw new Error('EarTraining payload must be an object');
  const data = raw as Partial<EarTrainingPayload>;
  if (!data.title) throw new Error('EarTraining requires title');

  const kind =
    data.kind === 'interval' || data.kind === 'chord' || data.kind === 'melody' ? data.kind : 'rhythm';

  const rhythmPattern = normalizeRhythmPattern(data.rhythmPattern);
  const patternTokens =
    rhythmPattern?.pattern ?? normalizeTokenArray(data.patternTokens);
  const rhythmMethod =
    data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly' ? data.rhythmMethod : 'tradicional';
  const prefs = { readingMethod: rhythmMethod, graphicMode: data.graphicMode === 'symbols-labels' ? 'symbols-labels' as const : 'symbols' as const };

  let teacherScript = String(data.teacherScript || '').trim();
  if (!teacherScript && patternTokens.length) {
    teacherScript = patternToTeacherScript(patternTokens, prefs);
  }
  if (!teacherScript && data.noteNames?.length) {
    teacherScript = `Dictado docente: toque ${data.noteNames.join(' — ')}`;
  }
  if (!teacherScript) teacherScript = 'Dictado docente: ejecute el fragmento indicado en la sala.';

  const noteNames = Array.isArray(data.noteNames) ? data.noteNames.map(String) : undefined;
  const frequencies =
    Array.isArray(data.frequencies) && data.frequencies.length
      ? data.frequencies.map(Number)
      : noteNames
        ? frequenciesFromNoteNames(noteNames)
        : undefined;

  const options = Array.isArray(data.options)
    ? data.options
        .map((opt) => {
          if (typeof opt === 'string') return opt.trim();
          if (opt && typeof opt === 'object') {
            const row = opt as Record<string, unknown>;
            return String(row.label ?? row.text ?? row.name ?? row.value ?? '').trim();
          }
          return String(opt ?? '').trim();
        })
        .filter((opt) => opt && opt !== '[object Object]')
    : [];
  const correctIndex = Math.max(0, Number(data.correctIndex) || 0);

  return {
    title: String(data.title),
    kind,
    teacherScript,
    studentPrompt: String(data.studentPrompt || 'Escucha y responde:').trim(),
    patternTokens: patternTokens.length ? patternTokens : undefined,
    rhythmPattern: rhythmPattern ?? (patternTokens.length ? { timeSignature: '4/4', pattern: patternTokens } : undefined),
    noteNames,
    frequencies,
    options: options.length ? options : undefined,
    correctIndex,
    rhythmMethod,
    graphicMode: prefs.graphicMode,
  };
}

export function createInitialEarTrainingRuntime(
  _payload: EarTrainingPayload,
  _routing: RoutingMode,
): EarTrainingRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0 } };
}

export const earTrainingLogic: ActivityLogicPlugin<'ear_training'> = {
  mode: 'ear_training',
  validatePayload: validateEarTrainingPayload,
  createInitialRuntime: createInitialEarTrainingRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'FILL') {
      const data = input.data as { value?: string | number };
      return {
        runtime: {
          ...ctx.runtime,
          responses: { ...ctx.runtime.responses, [participant.participantId]: data.value ?? '' },
        },
      };
    }
    if (input.action === 'SUBMIT') {
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      const submitted = Object.keys(finalized).filter((id) => finalized[id]).length;
      return {
        runtime: { ...ctx.runtime, finalized, aggregates: { submitted } },
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoEarTrainingPayload(): EarTrainingPayload {
  // Neutro — sin tríadas Do-Mi-Sol hardcodeadas (fuga tonal vía defaultPayloadForMode).
  return validateEarTrainingPayload({
    title: 'Dictado auditivo',
    kind: 'rhythm',
    teacherScript: 'Dictado docente: ejecute el fragmento indicado en la sala.',
    studentPrompt: 'Escucha y marca la opción correcta.',
    options: ['Opción 1', 'Opción 2', 'Opción 3'],
    correctIndex: 0,
  });
}
